import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  IBillingAccount,
  IBillingDocument,
  IOdbBillCategoryDetailSection,
} from '@dch/billing-business-logic';
import { OVERVIEW_PAGE } from '../../config/my-bill.constants';
import { ISelectedThirdPartySectionInfo } from '../../models/pii.model';
import {
  ODB_THIRD_PARTY_L4_PAGE_SESSION_DATA_KEY,
  UtilsService,
} from '../../services/utils.service';
import { BillingAnalyticsService } from '../../services/billing-analytics.service';
import { Store } from '@ngrx/store';
import { getOdbUsageLevel4Request } from 'src/app/state/actions';
import { ApplicationState } from 'src/app/state/reducers';
import { OnDestroyHookDirective } from '../../common/hooks';
import { filter, tap } from 'rxjs/operators';
import { selectOdbUsageLevel4ByDocumentReferenceAndIdentifierSelector } from 'src/app/state/selectors/odb-usage-level-4.selectors';
import { ICdrDetails } from '../../models/odb-usage-level-4.model';
import { MessageBoxTypesEnum } from '@dch/pxs-angular-design-system/src/lib/messages/message-box';

@Component({
  selector: 'my-bill-odb-third-party-level-4-page',
  templateUrl: './odb-third-party-level-4-page.component.html',
  standalone: false,
})
export class OdbThirdPartyLevel4PageComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  billingAccount?: IBillingAccount;
  billingDocument?: IBillingDocument;
  openTransactionToPayIndex?: number;
  productSplitIdentifier: string;
  selectedSection?: IOdbBillCategoryDetailSection;
  hasDataAvailable = false;
  customerId?: number;
  customerSubsegment?: string;
  customerGlobalId?: string;
  customerMobile?: string;
  cdrDetails: ICdrDetails[];
  hasError: boolean;
  isFetching: boolean;
  MessageBoxTypesEnum = MessageBoxTypesEnum;

  constructor(
    private utilsService: UtilsService,
    private router: Router,
    private billingAnalyticsService: BillingAnalyticsService,
    private store: Store<ApplicationState>
  ) {
    super();
  }

  ngOnInit() {
    const selectedThirdPartySectionInfo =
      this.utilsService.getInfoFromSessionStorage<ISelectedThirdPartySectionInfo>(
        ODB_THIRD_PARTY_L4_PAGE_SESSION_DATA_KEY
      );

    this.hasDataAvailable = Boolean(
      selectedThirdPartySectionInfo?.billDocument &&
        selectedThirdPartySectionInfo?.selectedSection
    );

    if (this.hasDataAvailable) {
      this.extractSelectedThirdPartySectionInfo(selectedThirdPartySectionInfo);
      this.billingDocument = selectedThirdPartySectionInfo.billDocument;
      this.selectedSection = selectedThirdPartySectionInfo.selectedSection;
      this.billingAccount = selectedThirdPartySectionInfo.billingAccount;
      if (this.billingAccount) {
        const billableIdentifierNumbers = this.selectedSection.items
          .map((items) => {
            return items.technicalIdentifier
              ? Array.prototype.map
                  .call(items.technicalIdentifier, (item) =>
                    encodeURIComponent(item)
                  )
                  .toString()
              : '';
          })
          .toString();

        this.dispatchThirdPartyLevel4Request(
          this.billingDocument.reference,
          billableIdentifierNumbers,
          selectedThirdPartySectionInfo.selectedSectionIdentifier,
          this.billingAccount.accountNumber
        );
        this.subscribeToOdbUsageLevel4(
          this.billingDocument.reference,
          selectedThirdPartySectionInfo.selectedSectionIdentifier
        );
      }
      this.getPageImpressionTagging();
    } else {
      this.router.navigate([OVERVIEW_PAGE]);
    }
  }

  private extractSelectedThirdPartySectionInfo(
    selectedThirdPartySectionInfo: ISelectedThirdPartySectionInfo
  ) {
    this.openTransactionToPayIndex =
      selectedThirdPartySectionInfo.openTransactionToPayIndex;
    this.productSplitIdentifier =
      selectedThirdPartySectionInfo.productSplitIdentifier;

    this.customerId = selectedThirdPartySectionInfo.customerId;
    this.customerGlobalId = selectedThirdPartySectionInfo.customerGlobalId;
    this.customerSubsegment = selectedThirdPartySectionInfo.customerSubsegment;
    this.customerMobile = selectedThirdPartySectionInfo.customerMobile;
  }

  private getPageImpressionTagging() {
    this.billingAnalyticsService.reportPageImpressionEvent(
      'detail.thirdParty.details',
      this.billingDocument.reference,
      this.billingAccount.accountNumber,
      'detail.thirdParty.details.' + (this.selectedSection.type ?? 'lvl4'),
      this.customerId,
      this.customerSubsegment,
      this.customerGlobalId,
      this.customerMobile
    );
  }

  private dispatchThirdPartyLevel4Request(
    documentReference: string,
    billableIdentifierNumber: string,
    sectionOrItemIdentifier: string,
    paNumber: string
  ) {
    this.store.dispatch(
      getOdbUsageLevel4Request({
        documentReference,
        billableIdentifierNumber: billableIdentifierNumber,
        sectionOrItemIdentifier,
        paNumber,
      })
    );
  }

  private subscribeToOdbUsageLevel4(
    documentReference: string,
    sectionOrItemIdentifier: string
  ) {
    this.addSubscription(
      this.store
        .select(
          selectOdbUsageLevel4ByDocumentReferenceAndIdentifierSelector(
            documentReference,
            sectionOrItemIdentifier
          )
        )
        .pipe(
          filter((odbUsageLevel4State) => odbUsageLevel4State !== undefined),
          tap((odbUsageLevel4State) => {
            this.cdrDetails = odbUsageLevel4State.result?.cdrDetails;
            this.hasError = odbUsageLevel4State.hasError;
            this.isFetching = odbUsageLevel4State.isFetching;
          })
        )
        .subscribe()
    );
  }
}
